export interface ProtocolEvent {
  direction: 'inbound' | 'outbound';
  timestamp: number;
  perfTime: number;
  messageType: string;
  messageKeys: string[];
  payloadSize: number;
  wsReadyState?: number;
  wsBufferedAmount?: number;
  preview?: string;
}

const MAX_PREVIEW_CHARS = 200;

const estimatePayloadSize = (payload: unknown): number => {
  if (typeof payload === 'string') {
    return payload.length;
  }
  try {
    return JSON.stringify(payload).length;
  } catch {
    return 0;
  }
};

const toPreview = (payload: unknown): string | undefined => {
  if (payload == null) return undefined;
  const raw = typeof payload === 'string' ? payload : JSON.stringify(payload);
  if (!raw) return undefined;
  return raw.length > MAX_PREVIEW_CHARS ? `${raw.slice(0, MAX_PREVIEW_CHARS)}...` : raw;
};

const inferMessageType = (payload: unknown): { messageType: string; messageKeys: string[] } => {
  if (!payload || typeof payload !== 'object') {
    return { messageType: typeof payload, messageKeys: [] };
  }
  const messageKeys = Object.keys(payload as Record<string, unknown>);
  if (messageKeys.length === 0) return { messageType: 'empty', messageKeys };
  return { messageType: messageKeys[0], messageKeys };
};

export class ProtocolRingBuffer {
  private buffer: ProtocolEvent[] = [];
  private readonly maxSize: number;

  constructor(maxSize = 100) {
    this.maxSize = maxSize;
  }

  log(
    direction: 'inbound' | 'outbound',
    payload: unknown,
    wsMeta?: { readyState?: number; bufferedAmount?: number }
  ): void {
    const inferred = inferMessageType(payload);
    const event: ProtocolEvent = {
      direction,
      timestamp: Date.now(),
      perfTime: typeof performance !== 'undefined' ? performance.now() : Date.now(),
      messageType: inferred.messageType,
      messageKeys: inferred.messageKeys,
      payloadSize: estimatePayloadSize(payload),
      wsReadyState: wsMeta?.readyState,
      wsBufferedAmount: wsMeta?.bufferedAmount,
      preview: toPreview(payload),
    };

    this.buffer.push(event);
    if (this.buffer.length > this.maxSize) {
      this.buffer.splice(0, this.buffer.length - this.maxSize);
    }
  }

  getAll(): ProtocolEvent[] {
    return [...this.buffer];
  }

  getRecent(count: number): ProtocolEvent[] {
    if (count <= 0) return [];
    return this.buffer.slice(-count);
  }

  clear(): void {
    this.buffer = [];
  }
}
